<template>
    <div class="ans-calendar-selected-date" :style="{ width: calendarWidth }">
        <!-- Calendar Header Toolbar -->
        <div
            :class="{
                'calendar-header-toolbar': true,
                ...(customizeConfig.headerToolbarClass || {})
            }"
            :style="customizeConfig.headerToolbarStyle || {}"
        >
            <!-- Left Navigation Button -->
            <template v-if="!readonly">
                <button
                    v-for="(navButton, index) in leftNavigationButtons"
                    :key="`nav-left-${index}`"
                    class="nav-btn"
                    :title="navButton.title"
                    @click="executeNavigationAction(navButton.action, navButton.params)"
                >
                    <i :class="navButton.icon"></i>
                </button>
            </template>

            <!-- Title in Center -->
            <div class="calendar-title">{{ calendarTitle }}</div>

            <!-- Right Navigation Button -->
            <template v-if="!readonly">
                <button
                    v-for="(navButton, index) in rightNavigationButtons"
                    :key="`nav-right-${index}`"
                    class="nav-btn"
                    :title="navButton.title"
                    @click="executeNavigationAction(navButton.action, navButton.params)"
                >
                    <i :class="navButton.icon"></i>
                </button>
            </template>
        </div>

        <!-- Calendar Grid -->
        <div
            :class="{
                'calendar-grid': true,
                ...(customizeConfig.calendarGridClass || {})
            }"
            :style="customizeConfig.calendarGridStyle || {}"
        >
            <!-- Weekday Headers (Optional) -->
            <div
                v-if="showWeekdayHeader"
                :class="{
                    'calendar-weekday-header': true,
                    ...(customizeConfig.weekdayHeaderClass || {})
                }"
                :style="customizeConfig.weekdayHeaderStyle || {}"
            >
                <div v-for="(nameDay, index) in ['日', '月', '火', '水', '木', '金', '土']" :key="index" class="weekday-cell">
                    {{ nameDay }}
                </div>
            </div>

            <!-- Calendar Body -->
            <div
                :class="{
                    'calendar-body': true,
                    ...(customizeConfig.calendarBodyClass || {})
                }"
                :style="customizeConfig.calendarBodyStyle || {}"
            >
                <div
                    v-for="(day, index) in monthViewDays"
                    :key="index"
                    :class="{
                        'calendar-day-cell': true,
                        ...getDayCellClasses(day),
                        'is-selected': isDateSelected(day.fullDate),
                        'is-readonly': readonly
                    }"
                    :style="getCellStyle(day)"
                    @click="!readonly && handleDayClick(day)"
                >
                    <div class="day-cell-content">
                        {{ day.date }}
                    </div>
                </div>
            </div>
        </div>

        <!-- Footer -->
        <div v-if="footerConfig.show" class="calendar-footer" :class="footerConfig.class || {}" :style="footerConfig.style || {}">
            <component
                v-if="footerConfig.component"
                :is="footerConfig.component"
                v-bind="footerConfig.componentProps || {}"
                v-on="footerConfig.componentEvents || {}"
            />
            <span v-else>{{ footerConfig.text || '※クリックで選択' }}</span>
        </div>
    </div>
</template>

<script>
/**
 * ****************************************************************************
 * @description     :   Calendar Component for Date Selection
 * @created at      :   2025/10/30
 * @created by      :   DaoNX
 * @package         :   Move
 * @copyright       :   Copyright (c) A.N.S Asia
 * @version         :   1.0.0
 * ****************************************************************************
 */

import { formatDate, getMonthViewDays, getDayCellClasses, isSameDate } from '@/utils/calendarHelpers';

export default {
    name: 'ansCalendarSelectedDate',
    emits: ['update:selectedDates', 'month-change', 'date-select'],
    props: {
        // Initial date to display calendar (default: current date)
        initialDate: {
            type: Date,
            default: () => new Date()
        },

        // Array of initially selected dates
        selectedDates: {
            type: Array,
            default: () => []
        },

        // Show weekday header (日月火水木金土)
        showWeekdayHeader: {
            type: Boolean,
            default: false
        },

        // Readonly mode (display only, no interaction)
        readonly: {
            type: Boolean,
            default: false
        },

        // Title format (YYYY/MM, YYYY年MM月, etc.)
        titleFormat: {
            type: String,
            default: 'YYYY/MM'
        },

        // Navigation configuration
        navigationConfig: {
            type: Object,
            default: () => ({
                buttons: [
                    {
                        action: 'navigateMonth',
                        params: [-1],
                        icon: 'fas fa-angle-left',
                        title: 'Previous Month'
                    },
                    {
                        action: 'navigateMonth',
                        params: [1],
                        icon: 'fas fa-angle-right',
                        title: 'Next Month'
                    }
                ]
            })
        },

        // Footer configuration
        footerConfig: {
            type: Object,
            default: () => ({
                show: true,
                text: '※クリックで選択',
                component: null,
                componentProps: {},
                componentEvents: {},
                class: {},
                style: {}
            })
        },

        // Hover background color
        hoverColor: {
            type: String,
            default: '#f0f0f0'
        },

        // Selected cell background color
        selectedColor: {
            type: String,
            default: '#e8380d'
        },

        // Calendar width
        calendarWidth: {
            type: String,
            default: '240px'
        },

        // Maximum number of dates that can be selected (null = no limit)
        maxSelectedDates: {
            type: Number,
            default: null
        },

        // Customization configuration (classes and styles)
        customizeConfig: {
            type: Object,
            default: () => ({
                headerToolbarClass: {},
                headerToolbarStyle: {},
                calendarGridClass: {},
                calendarGridStyle: {},
                weekdayHeaderClass: {},
                weekdayHeaderStyle: {},
                calendarBodyClass: {},
                calendarBodyStyle: {},
                dayCellClass: {},
                dayCellStyle: {}
            })
        }
    },

    data() {
        return {
            currentDate: new Date(this.initialDate),
            internalSelectedDates: [...this.selectedDates]
        };
    },

    computed: {
        calendarTitle() {
            return formatDate(this.currentDate, this.titleFormat);
        },

        monthViewDays() {
            return getMonthViewDays({
                currentDate: this.currentDate,
                selectedDate: null,
                events: []
            });
        },

        currentNavigationButtons() {
            return this.navigationConfig.buttons || [];
        },

        leftNavigationButtons() {
            const buttons = this.currentNavigationButtons;
            const halfPoint = Math.ceil(buttons.length / 2);
            return buttons.slice(0, halfPoint);
        },

        rightNavigationButtons() {
            const buttons = this.currentNavigationButtons;
            const halfPoint = Math.ceil(buttons.length / 2);
            return buttons.slice(halfPoint);
        }
    },

    methods: {
        // Handle day cell click
        handleDayClick(day) {
            // Don't allow selection of other month dates
            if (day.isOtherMonth) {
                return;
            }

            const clickedDate = new Date(day.fullDate);
            const index = this.internalSelectedDates.findIndex((date) => isSameDate(date, clickedDate));

            if (index > -1) {
                // Date is already selected, remove it (unselect)
                this.internalSelectedDates.splice(index, 1);
            } else {
                // Check if we've reached max selection limit
                if (this.maxSelectedDates !== null && this.internalSelectedDates.length >= this.maxSelectedDates) {
                    return;
                }

                // Add new selected date
                this.internalSelectedDates.push(clickedDate);
            }

            // Emit updated selected dates
            this.$emit('update:selectedDates', [...this.internalSelectedDates]);
            this.$emit('date-select', [...this.internalSelectedDates]);
        },

        // Check if a date is selected
        isDateSelected(date) {
            return this.internalSelectedDates.some((selectedDate) => isSameDate(selectedDate, date));
        },

        // Get cell style based on state
        getCellStyle(day) {
            const style = { ...this.customizeConfig.dayCellStyle };

            if (this.isDateSelected(day.fullDate) && !day.isOtherMonth) {
                style.backgroundColor = this.selectedColor;
                style.color = '#ffffff';
            }

            return style;
        },

        // Navigate by month
        navigateMonth(numberMonth = 1) {
            const newCurrentDate = new Date(this.currentDate);
            newCurrentDate.setMonth(newCurrentDate.getMonth() + numberMonth);
            this.currentDate = newCurrentDate;

            // Clear selected dates when navigating
            this.internalSelectedDates = [];
            this.$emit('update:selectedDates', []);

            // Emit month change event
            this.$emit('month-change', {
                year: newCurrentDate.getFullYear(),
                month: String(newCurrentDate.getMonth() + 1).padStart(2, '0')
            });
        },

        // Execute navigation action
        executeNavigationAction(action, params) {
            const method = this[action];
            if (typeof method === 'function') {
                method.apply(this, params);
            }
        },

        // Get day cell CSS classes
        getDayCellClasses(day) {
            return getDayCellClasses(day);
        }
    },

    watch: {
        // Watch for initialDate prop changes
        initialDate: {
            handler(newDate) {
                this.currentDate = new Date(newDate);
                // Clear selected dates when initial date changes
                this.internalSelectedDates = [];
                this.$emit('update:selectedDates', []);
            },
            deep: true
        },

        // Watch for selectedDates prop changes (sync from parent)
        selectedDates: {
            handler(newDates) {
                this.internalSelectedDates = [...newDates];
            },
            deep: true
        }
    }
};
</script>

<style lang="scss" scoped>
@use '@/assets/scss/common/var' as *;

.ans-calendar-selected-date {
    font-size: 12px;
    user-select: none;

    // Header toolbar
    .calendar-header-toolbar {
        display: flex;
        align-items: center;
        justify-content: center;
        color: $white;
        background-color: $label-background-color;

        .nav-btn {
            background: transparent;
            border: none;
            color: $white;
            cursor: pointer;
            padding: 4px 8px;
            font-size: 14px;
            transition: opacity 0.2s;

            &:hover {
                opacity: 0.7;
            }

            i {
                font-size: 1em;
            }
        }
    }

    // Calendar grid
    .calendar-grid {
        border: 1px solid $border--common;
        border-top: none;

        // Weekday header (optional)
        .calendar-weekday-header {
            display: grid;
            grid-template-columns: repeat(7, 1fr);
            background-color: #f8f9fa;

            .weekday-cell {
                padding: 6px 4px;
                text-align: center;
                font-weight: bold;
                font-size: 11px;
                border-right: 1px solid $border--common;
                border-bottom: 1px solid $border--common;

                &:last-child {
                    border-right: none;
                }
            }
        }

        // Calendar body
        .calendar-body {
            display: grid;
            grid-template-columns: repeat(7, 1fr);

            .calendar-day-cell {
                padding: 6px;
                text-align: right;
                border-right: 1px solid $border--common;
                border-bottom: 1px solid $border--common;
                cursor: pointer;
                transition: background-color 0.2s;
                font-size: 12px;

                &:nth-child(7n) {
                    border-right: none;
                }

                &:nth-last-child(-n + 7) {
                    border-bottom: none;
                }

                .day-cell-content {
                    user-select: none;
                }

                // State classes
                &.is-other-month {
                    background-color: $body--background--color;
                    cursor: not-allowed;

                    .day-cell-content {
                        opacity: 0.1;
                    }
                }

                &.is-sunday {
                    background-color: #fff0f5;
                }

                &.is-saturday {
                    background-color: #f0ffff;
                }

                &.is-holiday {
                    background-color: #fff0f5;
                }

                // Hover effect (only for selectable dates)
                &:hover:not(.is-other-month):not(.is-selected):not(.is-readonly) {
                    background-color: #f0f0f0;
                }

                // Selected state
                &.is-selected {
                    font-weight: bold;
                }

                // Readonly state
                &.is-readonly {
                    cursor: default;

                    &:hover {
                        background-color: inherit;
                    }
                }
            }
        }
    }
}
</style>
